import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getEnterpriseById } from "../../actions/enterprise.action";
import {
  closeCreditLineLoanByLoanId,
  getLoanById,
  getRetailOdLedgerInfoByLoanId,
  getRetailOdWriteOffCollectionsByLoanId,
  getTermLoanLedgerInfoByLoanId,
  getTermLoanWriteOffCollectionsByLoanId,
  getUnpaidRepaymentCommitmentAmountByEntityId,
  writeOffRetailOdByLoanId,
  writeOffTermLoanByLoanId
} from "../../actions/loan.action";
import { getUserById } from "../../actions/user.action";

import { showNotifier } from "../../actions/ui.action";
import { ConfirmDialogType } from "../../components/confirm-dialog";
import Tabs, { TabItem } from "../../components/tabs";
import {
  RepaymentCommitment,
  RetailODDetails,
  RetailOdLedgerInfoDto,
  TermLoanDetails,
  TermLoanLedgerInfoDto,
  UserDetails
} from "../../dto";
import { EnterpriseDetails } from "../../dto/enterprise.dto";
import useQueryParams from "../../hooks/useQueryParams";
import useScreenName from "../../hooks/useScreenName";
import { ReactComponent as Loader } from "../../images/loader.svg";
import dispatch from "../../middleware";
import { LoanType, NotifierBgColor } from "../../models";
import { ReduxState } from "../../reducers";
import CreditAccountSummary from "./creditAccountSummary";
import DrawdownContainer from "./drawdowns";
import { assertWriteOffFieldsValid } from "./helper";
import { useRepaymentCommitments } from "./hooks/useRepaymentCommitments";
import LoanDetailsHeader from "./loan-details/loanDetailsHeader";
import LoanDocuments from "./loan-documents";
import LoanDetailsSection from "./loanDetailsSection";
import { WriteOffCollectionAmount, WriteOffLoanFields } from "./types";

type TabIndex =
  | "LOAN_DETAILS"
  | "CREDIT_ASSESSMENT"
  | "DOCUMENTS"
  | "CREDIT_LINE_DETAILS"
  | "DRAWDOWNS";

type QueryState = {
  tab: TabIndex;
  loanType: LoanType;
};

const DEFAULT_QUERY_STATE: QueryState = {
  tab: "LOAN_DETAILS",
  loanType: "TERM_LOAN",
};

const tabItems: Record<
  Exclude<LoanType, "COMMERCIAL_OD" | "WORKING_CAPITAL_TERM_LOAN">,
  TabItem<TabIndex>[]
> = {
  TERM_LOAN: [
    {
      label: "Loan Details",
      value: "LOAN_DETAILS",
    },
    { label: "Documents", value: "DOCUMENTS" },
  ],
  RETAIL_OD: [
    {
      label: "Earned Wage Access Details",
      value: "CREDIT_LINE_DETAILS",
    },
    {
      label: "Drawdowns",
      value: "DRAWDOWNS",
    },
    { label: "Documents", value: "DOCUMENTS" },
  ],
};

export default function PersonalLoanDetailsContainer() {
  useScreenName("Personal Loans");
  const { loanId }: { loanId: string } = useParams();
  const storeDispatch = useDispatch();
  const loanDetails: TermLoanDetails | RetailODDetails | null = useSelector(
    (state: ReduxState) => state.loan.singleLoan,
  );
  const singleUser: UserDetails | null = useSelector(
    (state: ReduxState) => state.user.singleUser,
  );
  const enterpriseDetails: EnterpriseDetails | null = useSelector(
    (state: ReduxState) => state.enterprise.singleEnterprise,
  );
  const termLoanLedgerInfo: TermLoanLedgerInfoDto | null = useSelector(
    (state: ReduxState) => state.loan.termLoanLedgerInfo,
  );
  const retailOdLedgerInfo: RetailOdLedgerInfoDto | null = useSelector(
    (state: ReduxState) => state.loan.retailOdLedgerInfo,
  );
  const unpaidRepaymentCommitmentAmount: number | null = useSelector(
    (state: ReduxState) => state.loan.unpaidRepaymentCommitmentAmount,
  );
  const repaymentCommitments: RepaymentCommitment[] | null = useSelector(
    (state: ReduxState) => state.loan.repaymentCommitments,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setErrorMessage] = useState("");
  const [writeOffCollectionAmount, setWriteOffCollectionAmount] =
    useState<WriteOffCollectionAmount | null>(null);
  const [errMessage, setErrMessage] = useState("");

  const userDetails = loanDetails?.enterpriseId
    ? enterpriseDetails
    : singleUser;

  const [{ tab }, setSearchQuery] =
    useQueryParams<QueryState>(DEFAULT_QUERY_STATE);
  const { fetchLoanRepaymentCommitments } = useRepaymentCommitments();

  const fetchLoanDetails = useCallback(
    async (loanIdToBeFetched: string) => {
      try {
        const resp: TermLoanDetails | RetailODDetails = await dispatch(
          storeDispatch,
          getLoanById(loanIdToBeFetched),
        );
        if (
          !!resp.enterpriseId &&
          resp.enterpriseId !== enterpriseDetails?._id
        ) {
          await dispatch(storeDispatch, getEnterpriseById(resp.enterpriseId));
        }
        if (!!resp.userId) {
          await dispatch(storeDispatch, getUserById(resp.userId));
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";
        setErrorMessage(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch, enterpriseDetails?._id],
  );

  const fetchLedgerInfo = useCallback(async () => {
    try {
      if (!loanDetails || !loanId) return;

      loanDetails.loanType === "TERM_LOAN"
        ? await dispatch(storeDispatch, getTermLoanLedgerInfoByLoanId(loanId))
        : await dispatch(storeDispatch, getRetailOdLedgerInfoByLoanId(loanId));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setErrorMessage(errorMessage);
    }
  }, [loanDetails, loanId, storeDispatch]);

  async function writeOffRetailOdLoan(
    loanId: string,
    body: WriteOffLoanFields,
  ) {
    try {
      const response: RetailODDetails | null = await dispatch(
        storeDispatch,
        writeOffRetailOdByLoanId(loanId, body),
      );

      if (response) {
        fetchLoanDetails(loanId);
      }
    } catch (error) {
      showNotifier(
        NotifierBgColor.ERROR,
        error?.message || "An unknown error occurred",
      );
    }
  }

  async function writeOffTermLoan(loanId: string, body: WriteOffLoanFields) {
    try {
      const response: TermLoanDetails | null = await dispatch(
        storeDispatch,
        writeOffTermLoanByLoanId(loanId, body),
      );

      if (response) {
        fetchLoanDetails(loanId);
      }
    } catch (error) {
      showNotifier(
        NotifierBgColor.ERROR,
        error?.message || "An unknown error occurred",
      );
    }
  }

  async function handleWriteOffBtnClick(
    type: ConfirmDialogType,
    fieldData: WriteOffLoanFields,
  ) {
    try {
      const { writeOffDate, reason } = fieldData;
      assertWriteOffFieldsValid(fieldData);

      const formattedDate = new Date(writeOffDate).toISOString();
      const loanType = loanDetails?.loanType;
      loanType === "RETAIL_OD"
        ? await writeOffRetailOdLoan(loanId, {
            writeOffDate: formattedDate,
            reason,
          })
        : await writeOffTermLoan(loanId, {
            writeOffDate: formattedDate,
            reason,
          });
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Something went wrong";
      showNotifier(errorMsg, "error");
    }
  }

  const fetchLoanWriteOffCollectionAmount = useCallback(
    async (loanId: string) => {
      if (!loanDetails) return;
      try {
        const loanType = loanDetails.loanType;
        const action =
          loanType === "TERM_LOAN"
            ? getTermLoanWriteOffCollectionsByLoanId(loanId)
            : getRetailOdWriteOffCollectionsByLoanId(loanId);
        const response: WriteOffCollectionAmount = await dispatch(
          storeDispatch,
          action,
        );

        if (!response) return;

        setWriteOffCollectionAmount(response);
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error?.message || "An unknown error occurred",
        );
      }
    },
    [loanDetails, storeDispatch, setWriteOffCollectionAmount],
  );

  const fetchUnpaidRepaymentCommitmentAmount = useCallback(
    async (entityId: string) => {
      try {
        await dispatch(
          storeDispatch,
          getUnpaidRepaymentCommitmentAmountByEntityId(entityId),
        );
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error?.message || "An unknown error occurred",
        );
      }
    },
    [storeDispatch],
  );

  async function handleCloseCreditLineBtnClick(loanId: string) {
    if (!loanId) return;
    try {
      const response = await dispatch(
        storeDispatch,
        closeCreditLineLoanByLoanId(loanId),
      );

      if (response) fetchLoanDetails(loanId);
    } catch (error) {
      setErrMessage(error?.message || "Unable to close credit line loan");
    }
  }

  useEffect(() => {
    fetchLoanDetails(loanId);
  }, [loanId, fetchLoanDetails]);

  useEffect(() => {
    if (loanDetails) {
      fetchLedgerInfo();
    }
  }, [loanDetails, fetchLedgerInfo]);

  useEffect(() => {
    if (!loanId) return;
    fetchLoanWriteOffCollectionAmount(loanId);
  }, [fetchLoanWriteOffCollectionAmount, loanId]);

  useEffect(() => {
    if (!loanId || loanDetails?.loanType !== "TERM_LOAN") return;
    fetchUnpaidRepaymentCommitmentAmount(loanId);
  }, [fetchUnpaidRepaymentCommitmentAmount, loanDetails?.loanType, loanId]);

  useEffect(() => {
    if (!loanId && loanDetails?.loanType !== "TERM_LOAN") return;
    fetchLoanRepaymentCommitments(loanId);
  }, [fetchLoanRepaymentCommitments, loanDetails?.loanType, loanId]);

  if (isLoading || !loanDetails) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-container p-3">
        {(loanDetails.loanType === "TERM_LOAN" ||
          loanDetails.loanType === "RETAIL_OD") && (
          <Tabs
            items={tabItems[loanDetails.loanType]}
            value={tab}
            onChange={(value: TabIndex) => {
              setSearchQuery({
                ...DEFAULT_QUERY_STATE,
                tab: value,
                loanType: loanDetails.loanType,
              });
            }}
          />
        )}
      </div>

      {!isError && (
        <>
          <div className="dashboard-container">
            <LoanDetailsHeader loanDetails={loanDetails} />
          </div>
        </>
      )}

      {(tab === "LOAN_DETAILS" || tab === "CREDIT_LINE_DETAILS") && (
        <LoanDetailsSection
          errorMessage={errMessage}
          loanDetails={loanDetails}
          loanId={loanId}
          userDetails={userDetails}
          termLoanLedgerInfo={termLoanLedgerInfo}
          retailOdLedgerInfo={retailOdLedgerInfo}
          onWriteOffBtnClick={handleWriteOffBtnClick}
          writeOffCollectionAmount={writeOffCollectionAmount}
          unpaidRepaymentCommitmentAmount={unpaidRepaymentCommitmentAmount}
          repaymentCommitments={repaymentCommitments}
          onCloseCreditLineBtnClick={() =>
            handleCloseCreditLineBtnClick(loanId)
          }
        />
      )}
      {tab === "CREDIT_ASSESSMENT" && (
        <CreditAccountSummary loanType={"TERM_LOAN"} loanId={loanId} />
      )}
      {tab === "DOCUMENTS" && (
        <LoanDocuments
          loanType={
            loanDetails.loanType as Extract<LoanType, "TERM_LOAN" | "RETAIL_OD">
          }
          loanId={loanId}
        />
      )}
      {tab === "DRAWDOWNS" && <DrawdownContainer loanType={"DRAWDOWNS"} />}
    </>
  );
}
